// ✅ src/pages/VehicleV2/VehicleSaleScrapList.jsx  (UPDATED FULL FILE)
// Uses: app.use("/api/salesScrapList", salesScrapList);
//
// Expected endpoints:
//   GET /api/salesScrapList/init?branchId=&branchType=&loginUserId=
//   GET /api/salesScrapList/stores?branchId=&sessionBranchId=&branchType=&loginUserId=
//   GET /api/salesScrapList/list?branchId=&storeId=&vehicleMainType=&vehicleCode=&vehicleRegNo=&vehicleName=&page=&pageSize=&sessionBranchId=&branchType=&loginUserId=
// Optional autocomplete:
//   GET /api/salesScrapList/autocomplete/vehicleCode?prefix=&branchId=&branchType=&loginUserId=&storeId=
//   GET /api/salesScrapList/autocomplete/vehicleRegNo?prefix=&branchId=&branchType=&loginUserId=&storeId=
//   GET /api/salesScrapList/autocomplete/vehicleName?prefix=&branchId=&branchType=&loginUserId=&storeId=
//
// ✅ Updated pattern:
// - common localStorage / sessionStorage header pattern
// - useMemo + useCallback pattern
// - pageLoading + initLoading + storeLoading + gridLoading states
// - safer init / store reload / search / view all flow
// - fixed stale-state issue in View All
// - avoids unnecessary repeated API calls
// - safer autocomplete debounce
// - pagination remains API-driven

import React, {
  useState,
  useContext,
  useEffect,
  useMemo,
  useRef,
  useCallback,
} from "react";
import axios from "axios";
import HeaderBar from "../../components/HeaderBar";
import VehicleLayoutWrapper from "../../components/VehicleLayoutWrapper";
import getCurrentSkin from "../../utils/getCurrentSkin";
import { ThemeContext } from "../../context/ThemeContext";
import { toast, ToastContainer } from "react-toastify";
import "react-toastify/dist/ReactToastify.css";
import { useNavigate } from "react-router-dom";
import {
  FaSearch,
  FaEye,
  FaFilter,
  FaArrowLeft,
  FaInfoCircle,
  FaMoneyBill,
  FaUndo,
  FaSyncAlt,
  FaTimes,
} from "react-icons/fa";

const API_BASE = "http://nissi-india.com/api/salesScrapList";

/* ---------------- helpers ---------------- */
const toStr = (v, def = "") =>
  v === null || v === undefined ? def : String(v).trim();

const toInt = (v, def = 0) => {
  const n = Number(v);
  return Number.isFinite(n) ? Math.trunc(n) : def;
};

const toList = (arr) => (Array.isArray(arr) ? arr : []);

const normalizeSelect = (v, def = "Select") => {
  const s = toStr(v, def);
  return s || def;
};

const defaultFilters = {
  branchId: "Select",
  storeId: "Select",
  vehicleMainType: "Select",
  vehicleCode: "",
  vehicleRegNo: "",
  vehicleName: "",
};

const VehicleSaleScrapList = () => {
  const navigate = useNavigate();
  const { isDark, skin, setSkin } = useContext(ThemeContext);
  const currentSkin = getCurrentSkin(skin);
  const { baseColor, baseShade } = currentSkin;

  const buttonClass = `bg-${baseColor}-${baseShade}`;
  const hoverClass = `hover:bg-${baseColor}-700`;

  /* ===========================
     shared local/session pattern
  =========================== */
  const finYearId = localStorage.getItem("finYearId") || "";
  const finYear = localStorage.getItem("finYear") || "";
  const branchIdRaw =
    localStorage.getItem("branchId") ||
    localStorage.getItem("Branchid") ||
    localStorage.getItem("branchid") ||
    "8";
  const branchType =
    localStorage.getItem("branchType") ||
    localStorage.getItem("branchtype") ||
    "HO";
  const userId =
    localStorage.getItem("userId") ||
    localStorage.getItem("loginuserid") ||
    "0";
  const loginUserId = sessionStorage.getItem("loginUserId") || userId || "0";
  const sessionBranchId = toInt(branchIdRaw, 8);

  const commonHeaders = useMemo(
    () => ({
      "x-finyearid": finYearId,
      "x-finyear": finYear,
      "x-branchid": String(sessionBranchId),
      "x-branch": branchType,
      "x-userid": userId,
    }),
    [finYearId, finYear, sessionBranchId, branchType, userId]
  );

  /* ===========================
     UI state
  =========================== */
  const [showFilters, setShowFilters] = useState(true);

  const [pageLoading, setPageLoading] = useState(false);
  const [initLoading, setInitLoading] = useState(false);
  const [storeLoading, setStoreLoading] = useState(false);
  const [gridLoading, setGridLoading] = useState(false);

  const [init, setInit] = useState({
    branches: [{ label: "Select", value: "Select" }],
    stores: [{ label: "Select", value: "Select" }],
    vehicleMainTypes: [{ label: "Select", value: "Select" }],
  });

  const [rows, setRows] = useState([]);
  const [total, setTotal] = useState(0);

  const [page, setPage] = useState(1);
  const pageSize = 10;

  const [filters, setFilters] = useState(defaultFilters);

  /* ===========================
     autocomplete
  =========================== */
  const [codeSug, setCodeSug] = useState([]);
  const [regSug, setRegSug] = useState([]);
  const [nameSug, setNameSug] = useState([]);

  const codeRef = useRef(null);
  const regRef = useRef(null);
  const nameRef = useRef(null);

  const isBusy = pageLoading || initLoading || storeLoading || gridLoading;

  /* ===========================
     classes
  =========================== */
  const cardClass = useMemo(
    () =>
      `p-6 mt-6 rounded-2xl shadow-xl border ${
        isDark
          ? "bg-gradient-to-br from-white/20 to-white/5 backdrop-blur-md border-white/10 text-white"
          : `bg-white ${currentSkin.glass} text-black`
      }`,
    [isDark, currentSkin]
  );

  const sectionClass = useMemo(
    () =>
      `rounded-xl p-6 mb-6 border ${
        isDark ? "bg-gray-800 border-gray-700" : "bg-gray-50 border-gray-200"
      }`,
    [isDark]
  );

  const inputClass = useMemo(
    () =>
      `w-full px-3 py-2 border rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent ${
        isDark
          ? "bg-gray-700/30 border-gray-600 text-white placeholder:text-gray-400"
          : "bg-white border-gray-300 text-gray-900 placeholder:text-gray-400"
      }`,
    [isDark]
  );

  const labelClass = useMemo(
    () =>
      `block text-sm font-medium mb-2 ${
        isDark ? "text-gray-300" : "text-gray-700"
      }`,
    [isDark]
  );

  const pillClass = useMemo(
    () =>
      `inline-flex items-center px-3 py-1 rounded-full text-xs font-semibold ${
        isDark ? "bg-blue-900/60 text-blue-100" : "bg-blue-100 text-blue-800"
      }`,
    [isDark]
  );

  /* ===========================
     helpers
  =========================== */
  const setField = useCallback((name, value) => {
    setFilters((prev) => ({ ...prev, [name]: value }));
  }, []);

  const resetFiltersOnly = useCallback(() => {
    setFilters({
      ...defaultFilters,
      branchId: branchType === "HO" ? "Select" : String(sessionBranchId),
    });
    setPage(1);
    setCodeSug([]);
    setRegSug([]);
    setNameSug([]);
  }, [branchType, sessionBranchId]);

  const normalizeOptionList = useCallback((arr, fallbackLabel = "Select") => {
    const list = toList(arr).map((x) => ({
      label: toStr(x.label ?? x.name ?? x.text ?? fallbackLabel, fallbackLabel),
      value: String(x.value ?? x.id ?? x.key ?? fallbackLabel),
    }));
    return list.length ? list : [{ label: fallbackLabel, value: fallbackLabel }];
  }, []);

  const buildListParams = useCallback(
    (override = {}, goPage = page) => ({
      branchId: toStr(override.branchId ?? filters.branchId),
      storeId: toStr(override.storeId ?? filters.storeId),
      vehicleMainType: toStr(
        override.vehicleMainType ?? filters.vehicleMainType
      ),
      vehicleCode: toStr(override.vehicleCode ?? filters.vehicleCode),
      vehicleRegNo: toStr(override.vehicleRegNo ?? filters.vehicleRegNo),
      vehicleName: toStr(override.vehicleName ?? filters.vehicleName),
      page: goPage,
      pageSize,
      sessionBranchId,
      branchType,
      loginUserId,
    }),
    [filters, page, pageSize, sessionBranchId, branchType, loginUserId]
  );

  /* ===========================
     INIT load
  =========================== */
  const loadInit = useCallback(async () => {
    try {
      setPageLoading(true);
      setInitLoading(true);

      const { data } = await axios.get(`${API_BASE}/init`, {
        params: {
          branchId: sessionBranchId,
          branchType,
          loginUserId,
        },
        headers: commonHeaders,
      });

      if (!data?.success) {
        toast.error(data?.message || "Failed to load init");
        return;
      }

      const branches = normalizeOptionList(data.branches, "Select");
      const stores = normalizeOptionList(data.stores, "Select");
      const vehicleMainTypes = normalizeOptionList(
        data.vehicleMainTypes,
        "Select"
      );

      setInit({
        branches,
        stores,
        vehicleMainTypes,
      });

      setFilters((prev) => ({
        ...prev,
        branchId: branchType === "HO" ? prev.branchId : String(sessionBranchId),
      }));
    } catch (err) {
      console.error(err);
      toast.error(err?.response?.data?.message || err.message || "Init failed");
    } finally {
      setInitLoading(false);
      setPageLoading(false);
    }
  }, [
    API_BASE,
    sessionBranchId,
    branchType,
    loginUserId,
    commonHeaders,
    normalizeOptionList,
  ]);

  /* ===========================
     stores by branch
  =========================== */
  const loadStoresForBranch = useCallback(
    async (branchId) => {
      const bid = toStr(branchId);
      if (!bid) return;

      try {
        setStoreLoading(true);

        const { data } = await axios.get(`${API_BASE}/stores`, {
          params: {
            branchId: bid,
            sessionBranchId,
            branchType,
            loginUserId,
          },
          headers: commonHeaders,
        });

        if (!data?.success) return;

        setInit((prev) => ({
          ...prev,
          stores: normalizeOptionList(data.stores, "Select"),
        }));
      } catch (err) {
        console.error(err);
      } finally {
        setStoreLoading(false);
      }
    },
    [
      sessionBranchId,
      branchType,
      loginUserId,
      commonHeaders,
      normalizeOptionList,
    ]
  );

  /* ===========================
     SEARCH (API)
  =========================== */
  const doSearch = useCallback(
    async ({ silent = false, goPage = page, overrideFilters = null } = {}) => {
      try {
        setGridLoading(true);

        const params = buildListParams(overrideFilters || {}, goPage);

        const { data } = await axios.get(`${API_BASE}/list`, {
          params,
          headers: commonHeaders,
        });

        if (!data?.success) {
          setRows([]);
          setTotal(0);
          if (!silent) toast.error(data?.message || "Search failed");
          return;
        }

        setRows(toList(data.list));
        setTotal(toInt(data.total, 0));
        setPage(toInt(data.page, goPage));

        if (!silent) {
          toast.success(`Found ${toInt(data.total, 0)} record(s)`);
        }
      } catch (err) {
        console.error(err);
        toast.error(err?.response?.data?.message || err.message || "Search failed");
        setRows([]);
        setTotal(0);
      } finally {
        setGridLoading(false);
      }
    },
    [page, buildListParams, commonHeaders]
  );

  /* ===========================
     handlers
  =========================== */
  const handleFilterChange = useCallback((e) => {
    const { name, value } = e.target;
    setField(name, value);
  }, [setField]);

  const handleClearFilters = useCallback(() => {
    resetFiltersOnly();
    setRows([]);
    setTotal(0);
    setPage(1);
    toast.info("Filters cleared!");
  }, [resetFiltersOnly]);

  const handleSearchClick = useCallback(async () => {
    await doSearch({ silent: false, goPage: 1 });
  }, [doSearch]);

  const handleViewAll = useCallback(async () => {
    const resetObj = {
      ...defaultFilters,
      branchId: branchType === "HO" ? "Select" : String(sessionBranchId),
    };

    setFilters(resetObj);
    setPage(1);
    setCodeSug([]);
    setRegSug([]);
    setNameSug([]);

    await doSearch({
      silent: true,
      goPage: 1,
      overrideFilters: resetObj,
    });

    toast.info("Showing all records");
  }, [branchType, sessionBranchId, doSearch]);

  const handleRefresh = useCallback(async () => {
    await doSearch({ silent: true, goPage: page });
  }, [doSearch, page]);

  const handleBack = useCallback(() => {
    navigate(-1);
  }, [navigate]);

  const handleViewDetails = useCallback((row) => {
    const vehicleId = row?.fld_vehicleid || row?.VehicleId;

    if (!vehicleId) {
      toast.error("Vehicle ID not found");
      return;
    }

    window.open(
      `/vehicle/OwnVehicle_DetailsEntry?vehicleId=${vehicleId}`,
      "_blank",
      "width=900,height=600,scrollbars=yes,resizable=no"
    );
  }, []);

  const handleSaleScrap = useCallback(
    (row) => {
      navigate("/vehicle/Vehicle_SalesandScrapEntry", {
        state: {
          vehicleId: row?.fld_vehicleid,
          vehicleOwnDetId: row?.fld_vehicleown_detid,
        },
      });
    },
    [navigate]
  );

  /* ===========================
     autocomplete
  =========================== */
  const debounce = (fn, ms = 300) => {
    let t;
    return (...args) => {
      clearTimeout(t);
      t = setTimeout(() => fn(...args), ms);
    };
  };

  const fetchCodeSug = useMemo(
    () =>
      debounce(async (prefix) => {
        const p = toStr(prefix);
        if (!p) {
          setCodeSug([]);
          return;
        }

        try {
          const { data } = await axios.get(`${API_BASE}/autocomplete/vehicleCode`, {
            params: {
              prefix: p,
              branchId:
                branchType === "HO"
                  ? filters.branchId
                  : String(sessionBranchId),
              branchType,
              loginUserId,
              storeId: filters.storeId,
            },
            headers: commonHeaders,
          });

          setCodeSug(Array.isArray(data?.list) ? data.list : []);
        } catch {
          setCodeSug([]);
        }
      }),
    [
      filters.branchId,
      filters.storeId,
      branchType,
      sessionBranchId,
      loginUserId,
      commonHeaders,
    ]
  );

  const fetchRegSug = useMemo(
    () =>
      debounce(async (prefix) => {
        const p = toStr(prefix);
        if (!p) {
          setRegSug([]);
          return;
        }

        try {
          const { data } = await axios.get(`${API_BASE}/autocomplete/vehicleRegNo`, {
            params: {
              prefix: p,
              branchId:
                branchType === "HO"
                  ? filters.branchId
                  : String(sessionBranchId),
              branchType,
              loginUserId,
              storeId: filters.storeId,
            },
            headers: commonHeaders,
          });

          setRegSug(Array.isArray(data?.list) ? data.list : []);
        } catch {
          setRegSug([]);
        }
      }),
    [
      filters.branchId,
      filters.storeId,
      branchType,
      sessionBranchId,
      loginUserId,
      commonHeaders,
    ]
  );

  const fetchNameSug = useMemo(
    () =>
      debounce(async (prefix) => {
        const p = toStr(prefix);
        if (!p) {
          setNameSug([]);
          return;
        }

        try {
          const { data } = await axios.get(`${API_BASE}/autocomplete/vehicleName`, {
            params: {
              prefix: p,
              branchId:
                branchType === "HO"
                  ? filters.branchId
                  : String(sessionBranchId),
              branchType,
              loginUserId,
              storeId: filters.storeId,
            },
            headers: commonHeaders,
          });

          setNameSug(Array.isArray(data?.list) ? data.list : []);
        } catch {
          setNameSug([]);
        }
      }),
    [
      filters.branchId,
      filters.storeId,
      branchType,
      sessionBranchId,
      loginUserId,
      commonHeaders,
    ]
  );

  /* ===========================
     effects
  =========================== */
  useEffect(() => {
    loadInit();
  }, [loadInit]);

  useEffect(() => {
    const currentBranch =
      branchType === "HO" ? filters.branchId : String(sessionBranchId);

    if (!currentBranch) return;
    if (currentBranch === "Select" && branchType === "HO") return;

    loadStoresForBranch(currentBranch);
    setFilters((prev) => ({ ...prev, storeId: "Select" }));
  }, [filters.branchId, branchType, sessionBranchId, loadStoresForBranch]);

  useEffect(() => {
    const onDocClick = (e) => {
      if (codeRef.current && !codeRef.current.contains(e.target)) setCodeSug([]);
      if (regRef.current && !regRef.current.contains(e.target)) setRegSug([]);
      if (nameRef.current && !nameRef.current.contains(e.target)) setNameSug([]);
    };

    document.addEventListener("mousedown", onDocClick);
    return () => document.removeEventListener("mousedown", onDocClick);
  }, []);

  /* ===========================
     pagination
  =========================== */
  const totalPages = useMemo(
    () => Math.max(1, Math.ceil(total / pageSize)),
    [total, pageSize]
  );

  const goToPage = useCallback(
    async (p) => {
      const next = Math.max(1, Math.min(totalPages, p));
      await doSearch({ silent: true, goPage: next });
    },
    [doSearch, totalPages]
  );

  return (
    <VehicleLayoutWrapper>
      <div className={`min-h-screen p-6 ${isDark ? "bg-gray-900" : "bg-gray-100"}`}>
        <HeaderBar
          title={<span className="font-dmserif text-3xl">VEHICLE SALE OR SCRAP</span>}
          setSkin={setSkin}
          currentSkin={skin}
        />

        <div className={cardClass}>
          {/* Header */}
          <div className="flex flex-col md:flex-row md:items-center justify-between gap-4 mb-8">
            <div>
              <h1 className={`text-2xl font-bold ${isDark ? "text-white" : "text-gray-900"}`}>
                Vehicle Sales and Scrap Listing
              </h1>

              <div className={`text-sm mt-1 ${isDark ? "text-gray-300" : "text-gray-600"}`}>
                Branch ID: {sessionBranchId || "-"} {" • "} User ID: {loginUserId || "-"}
              </div>

              <div className="mt-2 flex flex-wrap gap-2">
                <span className={pillClass}>Records: {total}</span>
                <span className={pillClass}>Page: {page}</span>
                <span className={pillClass}>Branch Type: {branchType || "-"}</span>
              </div>
            </div>

            <div className="flex flex-wrap items-center gap-3">
              <button
                onClick={() => setShowFilters((s) => !s)}
                disabled={isBusy}
                className={`flex items-center gap-2 px-4 py-2 border rounded-lg transition-colors ${
                  isDark
                    ? "border-gray-600 text-white hover:bg-gray-800" +
                      (showFilters ? " bg-gray-800" : "")
                    : "border-gray-300 text-gray-700 hover:bg-gray-50" +
                      (showFilters ? " bg-gray-50" : "")
                } ${isBusy ? "opacity-60 cursor-not-allowed" : ""}`}
              >
                <FaFilter className="w-4 h-4" />
                {showFilters ? "Hide Filters" : "Show Filters"}
              </button>

              <button
                onClick={handleRefresh}
                disabled={isBusy}
                className={`flex items-center gap-2 px-4 py-2 border rounded-lg transition-colors ${
                  isDark
                    ? "border-gray-600 text-white hover:bg-gray-800"
                    : "border-gray-300 text-gray-700 hover:bg-gray-50"
                } ${isBusy ? "opacity-60 cursor-not-allowed" : ""}`}
              >
                <FaSyncAlt className="w-4 h-4" />
                Refresh
              </button>

              <button
                onClick={handleBack}
                disabled={isBusy}
                className={`flex items-center gap-2 px-4 py-2 border rounded-lg transition-colors ${
                  isDark
                    ? "border-gray-600 text-white hover:bg-gray-800"
                    : "border-gray-300 text-gray-700 hover:bg-gray-50"
                } ${isBusy ? "opacity-60 cursor-not-allowed" : ""}`}
              >
                <FaArrowLeft className="w-4 h-4" />
                Back
              </button>
            </div>
          </div>

          {/* Filters */}
          {showFilters && (
            <div className={sectionClass}>
              <h3 className={`text-lg font-semibold mb-4 flex items-center gap-2 ${isDark ? "text-white" : "text-gray-900"}`}>
                <FaFilter /> FILTRATION AREA
              </h3>

              <div className="grid grid-cols-1 md:grid-cols-3 gap-6 mb-6">
                {/* Branch */}
                <div>
                  <label className={labelClass}>Branch</label>
                  <select
                    name="branchId"
                    value={filters.branchId}
                    onChange={handleFilterChange}
                    className={inputClass}
                    disabled={branchType !== "HO" || isBusy}
                  >
                    {(init.branches || []).map((b, idx) => (
                      <option key={idx} value={b.value}>
                        {b.label}
                      </option>
                    ))}
                  </select>
                </div>

                {/* Store */}
                <div>
                  <label className={labelClass}>Store</label>
                  <select
                    name="storeId"
                    value={filters.storeId}
                    onChange={handleFilterChange}
                    className={inputClass}
                    disabled={isBusy}
                  >
                    {(init.stores || []).map((s, idx) => (
                      <option key={idx} value={s.value}>
                        {s.label}
                      </option>
                    ))}
                  </select>
                </div>

                {/* Vehicle Main Type */}
                <div>
                  <label className={labelClass}>Vehicle Main Type</label>
                  <select
                    name="vehicleMainType"
                    value={filters.vehicleMainType}
                    onChange={handleFilterChange}
                    className={inputClass}
                    disabled={isBusy}
                  >
                    {(init.vehicleMainTypes || []).map((t, idx) => (
                      <option key={idx} value={t.value}>
                        {t.label}
                      </option>
                    ))}
                  </select>
                </div>

                {/* Vehicle Code */}
                <div ref={codeRef} className="relative">
                  <label className={labelClass}>Vehicle Code</label>
                  <input
                    type="text"
                    name="vehicleCode"
                    value={filters.vehicleCode}
                    onChange={(e) => {
                      handleFilterChange(e);
                      fetchCodeSug(e.target.value);
                    }}
                    className={inputClass}
                    placeholder="Enter code"
                    autoComplete="off"
                    disabled={isBusy}
                  />
                  {codeSug.length > 0 && (
                    <div
                      className={`absolute z-20 mt-1 w-full rounded-lg border shadow-lg overflow-hidden ${
                        isDark ? "bg-gray-900 border-gray-700" : "bg-white border-gray-200"
                      }`}
                    >
                      {codeSug.slice(0, 10).map((it, idx) => (
                        <button
                          type="button"
                          key={idx}
                          onClick={() => {
                            setFilters((p) => ({ ...p, vehicleCode: String(it) }));
                            setCodeSug([]);
                          }}
                          className={`w-full text-left px-3 py-2 text-sm ${
                            isDark
                              ? "text-gray-200 hover:bg-gray-800"
                              : "text-gray-800 hover:bg-gray-100"
                          }`}
                        >
                          {String(it)}
                        </button>
                      ))}
                    </div>
                  )}
                </div>

                {/* RegNo */}
                <div ref={regRef} className="relative">
                  <label className={labelClass}>Vehicle Registration Number</label>
                  <input
                    type="text"
                    name="vehicleRegNo"
                    value={filters.vehicleRegNo}
                    onChange={(e) => {
                      handleFilterChange(e);
                      fetchRegSug(e.target.value);
                    }}
                    className={inputClass}
                    placeholder="Enter Reg No"
                    autoComplete="off"
                    disabled={isBusy}
                  />
                  {regSug.length > 0 && (
                    <div
                      className={`absolute z-20 mt-1 w-full rounded-lg border shadow-lg overflow-hidden ${
                        isDark ? "bg-gray-900 border-gray-700" : "bg-white border-gray-200"
                      }`}
                    >
                      {regSug.slice(0, 10).map((it, idx) => (
                        <button
                          type="button"
                          key={idx}
                          onClick={() => {
                            setFilters((p) => ({ ...p, vehicleRegNo: String(it) }));
                            setRegSug([]);
                          }}
                          className={`w-full text-left px-3 py-2 text-sm ${
                            isDark
                              ? "text-gray-200 hover:bg-gray-800"
                              : "text-gray-800 hover:bg-gray-100"
                          }`}
                        >
                          {String(it)}
                        </button>
                      ))}
                    </div>
                  )}
                </div>

                {/* Name */}
                <div ref={nameRef} className="relative">
                  <label className={labelClass}>Vehicle Name</label>
                  <input
                    type="text"
                    name="vehicleName"
                    value={filters.vehicleName}
                    onChange={(e) => {
                      handleFilterChange(e);
                      fetchNameSug(e.target.value);
                    }}
                    className={inputClass}
                    placeholder="Enter name"
                    autoComplete="off"
                    disabled={isBusy}
                  />
                  {nameSug.length > 0 && (
                    <div
                      className={`absolute z-20 mt-1 w-full rounded-lg border shadow-lg overflow-hidden ${
                        isDark ? "bg-gray-900 border-gray-700" : "bg-white border-gray-200"
                      }`}
                    >
                      {nameSug.slice(0, 10).map((it, idx) => (
                        <button
                          type="button"
                          key={idx}
                          onClick={() => {
                            setFilters((p) => ({ ...p, vehicleName: String(it) }));
                            setNameSug([]);
                          }}
                          className={`w-full text-left px-3 py-2 text-sm ${
                            isDark
                              ? "text-gray-200 hover:bg-gray-800"
                              : "text-gray-800 hover:bg-gray-100"
                          }`}
                        >
                          {String(it)}
                        </button>
                      ))}
                    </div>
                  )}
                </div>
              </div>

              <div className="flex flex-col md:flex-row justify-between items-center gap-3 mt-6 pt-4 border-t border-gray-200 dark:border-gray-700">
                <button
                  onClick={handleClearFilters}
                  disabled={isBusy}
                  className={`flex items-center gap-2 font-medium transition-colors duration-200 ${
                    isDark
                      ? "text-gray-300 hover:text-white"
                      : "text-gray-600 hover:text-gray-900"
                  } ${isBusy ? "opacity-60 cursor-not-allowed" : ""}`}
                >
                  <FaUndo />
                  Clear All Filters
                </button>

                <div className="flex gap-3">
                  <button
                    onClick={handleSearchClick}
                    disabled={isBusy}
                    className={`flex items-center gap-2 ${buttonClass} ${hoverClass} text-white px-6 py-2 rounded-lg font-semibold shadow-lg hover:shadow-xl transform hover:-translate-y-0.5 transition-all duration-200 ${
                      isBusy ? "opacity-60 cursor-not-allowed" : ""
                    }`}
                  >
                    <FaSearch />
                    {gridLoading ? "Searching..." : "Search"}
                  </button>

                  <button
                    onClick={handleViewAll}
                    disabled={isBusy}
                    className={`flex items-center gap-2 px-6 py-2 border border-gray-300 dark:border-gray-600 rounded-lg font-semibold text-gray-700 dark:text-gray-300 hover:bg-gray-50 dark:hover:bg-gray-700 transition-colors ${
                      isBusy ? "opacity-60 cursor-not-allowed" : ""
                    }`}
                  >
                    <FaEye />
                    View All
                  </button>
                </div>
              </div>
            </div>
          )}

          {/* Results */}
          <div className={`rounded-xl shadow-sm border overflow-hidden ${isDark ? "border-gray-700" : "border-gray-200"}`}>
            {rows.length === 0 ? (
              <div className="text-center py-16">
                <div className={`inline-flex items-center justify-center w-20 h-20 rounded-full mb-6 ${isDark ? "bg-gray-700" : "bg-gray-100"}`}>
                  {gridLoading || initLoading ? (
                    <FaSearch className="w-10 h-10 text-gray-400" />
                  ) : (
                    <FaInfoCircle className="w-10 h-10 text-gray-400" />
                  )}
                </div>
                <h3 className={`text-xl font-semibold mb-2 ${isDark ? "text-gray-200" : "text-gray-800"}`}>
                  {gridLoading || initLoading ? "Loading..." : "No Vehicles Found"}
                </h3>
                <p className={`mb-6 max-w-sm mx-auto ${isDark ? "text-gray-400" : "text-gray-500"}`}>
                  {gridLoading || initLoading
                    ? "Please wait..."
                    : "Try adjusting your search criteria, then click Search."}
                </p>
              </div>
            ) : (
              <div className="overflow-x-auto">
                <table className="w-full">
                  <thead className={`${buttonClass} text-white`}>
                    <tr>
                      <th className="px-4 py-4 text-center font-semibold">S.No</th>
                      <th className="px-4 py-4 text-center font-semibold">Branch Name</th>
                      <th className="px-4 py-4 text-center font-semibold">Vehicle Main Type</th>
                      <th className="px-4 py-4 text-center font-semibold">Vehicle Code</th>
                      <th className="px-4 py-4 text-center font-semibold">Vehicle Name</th>
                      <th className="px-4 py-4 text-center font-semibold">Vehicle Registration No</th>
                      <th className="px-4 py-4 text-center font-semibold">Details</th>
                      <th className="px-4 py-4 text-center font-semibold">Sale or Scrap</th>
                    </tr>
                  </thead>

                  <tbody className="divide-y divide-gray-200 dark:divide-gray-700">
                    {rows.map((r, idx) => (
                      <tr
                        key={`${r?.fld_vehicleid || r?.VehicleId || idx}-${idx}`}
                        className={isDark ? "bg-gray-800 hover:bg-gray-700 transition-colors duration-200" : "bg-white hover:bg-gray-50 transition-colors duration-200"}
                      >
                        <td className={`px-4 py-4 whitespace-nowrap text-sm text-center ${isDark ? "text-gray-300" : "text-gray-900"}`}>
                          {(page - 1) * pageSize + idx + 1}
                        </td>

                        <td className={`px-4 py-4 whitespace-nowrap text-sm text-center ${isDark ? "text-gray-300" : "text-gray-900"}`}>
                          {r.fld_branchname ?? r.BranchName ?? ""}
                        </td>

                        <td className={`px-4 py-4 whitespace-nowrap text-sm text-center ${isDark ? "text-gray-300" : "text-gray-900"}`}>
                          {r.VehicleType ?? r.vehicleType ?? ""}
                        </td>

                        <td className={`px-4 py-4 whitespace-nowrap text-sm text-center ${isDark ? "text-gray-300" : "text-gray-900"}`}>
                          {r.fld_vehicle_code ?? r.VehicleCode ?? ""}
                        </td>

                        <td className={`px-4 py-4 whitespace-nowrap text-sm text-center ${isDark ? "text-gray-300" : "text-gray-900"}`}>
                          {r.fld_vehicle_name ?? r.VehicleName ?? ""}
                        </td>

                        <td className={`px-4 py-4 whitespace-nowrap text-sm text-center ${isDark ? "text-gray-300" : "text-gray-900"}`}>
                          {r.fld_vehicle_regno ?? r.VehicleRegNo ?? ""}
                        </td>

                        <td className="px-4 py-4 text-center">
                          <button
                            onClick={() => handleViewDetails(r)}
                            className={`inline-flex items-center gap-2 px-3 py-1 rounded text-sm ${
                              isDark
                                ? "bg-blue-700 text-white hover:bg-blue-600"
                                : "bg-blue-100 text-blue-800 hover:bg-blue-200"
                            }`}
                          >
                            <FaInfoCircle className="w-3 h-3" />
                            Details
                          </button>
                        </td>

                        <td className="px-4 py-4 text-center">
                          <button
                            onClick={() => handleSaleScrap(r)}
                            className={`inline-flex items-center gap-2 px-3 py-1 rounded text-sm ${
                              isDark
                                ? "bg-green-700 text-white hover:bg-green-600"
                                : "bg-green-100 text-green-800 hover:bg-green-200"
                            }`}
                          >
                            <FaMoneyBill className="w-3 h-3" />
                            Sale/Scrap
                          </button>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            )}
          </div>

          {/* Pagination */}
          {total > 0 && (
            <div className="mt-6 flex justify-center">
              <nav className="flex gap-1 flex-wrap justify-center">
                <button
                  onClick={() => goToPage(page - 1)}
                  disabled={page === 1 || isBusy}
                  className={`px-3 py-1 border rounded text-sm ${
                    isDark
                      ? "border-gray-600 text-gray-300 hover:bg-gray-700"
                      : "border-gray-300 text-gray-700 hover:bg-gray-100"
                  } disabled:opacity-50`}
                >
                  Prev
                </button>

                {Array.from({ length: totalPages }, (_, i) => (
                  <button
                    key={i + 1}
                    onClick={() => goToPage(i + 1)}
                    disabled={isBusy}
                    className={`px-3 py-1 border rounded text-sm ${
                      page === i + 1
                        ? `${buttonClass} text-white`
                        : isDark
                        ? "border-gray-600 text-gray-300 hover:bg-gray-700"
                        : "border-gray-300 text-gray-700 hover:bg-gray-100"
                    } ${isBusy ? "opacity-60 cursor-not-allowed" : ""}`}
                  >
                    {i + 1}
                  </button>
                ))}

                <button
                  onClick={() => goToPage(page + 1)}
                  disabled={page === totalPages || isBusy}
                  className={`px-3 py-1 border rounded text-sm ${
                    isDark
                      ? "border-gray-600 text-gray-300 hover:bg-gray-700"
                      : "border-gray-300 text-gray-700 hover:bg-gray-100"
                  } disabled:opacity-50`}
                >
                  Next
                </button>
              </nav>
            </div>
          )}

          {isBusy && (
            <div className={`mt-4 text-sm ${isDark ? "text-gray-300" : "text-gray-600"}`}>
              {pageLoading
                ? "Loading page..."
                : initLoading
                ? "Loading initial data..."
                : storeLoading
                ? "Loading stores..."
                : gridLoading
                ? "Loading records..."
                : "Loading..."}
            </div>
          )}

          <ToastContainer
            position="top-right"
            autoClose={2500}
            hideProgressBar={false}
            closeOnClick
            pauseOnHover
            theme={isDark ? "dark" : "light"}
          />
        </div>
      </div>
    </VehicleLayoutWrapper>
  );
};

export default VehicleSaleScrapList;